// Storage

export interface XferStorage {
  _id: string;
  created: string;
  last_status_check: string;
  modified: string;
  label: string;
  refs: {
    group?: string;
    project?: string;
    connector?: string;
  };
  url: string;
  config: StorageConfig;
  env?: {
    [variable: string]: string;
  };
  imports_enabled?: boolean;
  exports_enabled?: boolean;
  malware_scan?: boolean;
  status_check: StorageCheckStatus;
  status_check_reason?: string;
  notes?: string;
  kind?: XferStorageKind;
}

export enum StorageConfigType {
  S3 = 's3',
  GS = 'gs',
  AZ = 'az',
  FS = 'fs',
  AHI = 'ahi',
}

export interface AmazonS3Config {
  type: StorageConfigType.S3;
  bucket: string;
  prefix?: string;
  connector_creds?: boolean;
  secret_access_key?: string;
  access_key_id?: string;
}

export interface AHIConfig {
  type: StorageConfigType.AHI;
  datastore_id?: string;
  region?: string;
  prefix?: string;
  connector_creds?: boolean;
  secret_access_key?: string;
  access_key_id?: string;
}

export interface GoogleCloudStorageConfig {
  type: StorageConfigType.GS;
  bucket: string;
  prefix?: string;
  notes?: string;
  application_credentials?: string;
}

export interface AzureBlobStorageConfig {
  type: StorageConfigType.AZ;
  account: string;
  container: string;
  prefix?: string;
  connector_creds?: boolean;
  client_id?: string;
  client_secret?: string;
  tenant_id?: string;
  access_key?: string;
}

export interface FileSystemConfig {
  type: StorageConfigType.FS;
  path: string;
  chown?: string;
  chmod?: string;
  groups?: number[];
}

export type StorageConfig = AHIConfig | AmazonS3Config | GoogleCloudStorageConfig | AzureBlobStorageConfig | FileSystemConfig;

export interface AmazonS3ConfigOverride {
  type?: StorageConfigType.S3;
  prefix?: string;
}

export interface GoogleCloudStorageConfigOverride {
  type?: StorageConfigType.GS;
  prefix?: string;
}

export interface AzureBlobStorageConfigOverride {
  type?: StorageConfigType.AZ;
  prefix?: string;
}

export interface FileSystemConfigOverride {
  type?: StorageConfigType.FS;
  uid?: number;
  gid?: number;
  chmod?: string;
}

export type StorageConfigOverride =
  AmazonS3ConfigOverride | GoogleCloudStorageConfigOverride | AzureBlobStorageConfigOverride | FileSystemConfigOverride;

export enum StorageCheckStatus {
  Pending = 'pending',
  Running = 'running',
  Success = 'success',
  Failed = 'failed',
}

export enum XferStorageKind {
  Blob = 'blob',
  Dicom = 'dicom',
}

export interface StorageStatusHistory {
  status: StorageCheckStatus;
  timestamp: string;
}

export interface StorageCheckResponse extends Pick<XferStorage, '_id' | 'created' | 'modified' | 'refs'> {
  started: string;
  status: StorageCheckStatus;
  status_history: StorageStatusHistory[];
  status_reason: string;
  type: 'storage-check';
  priority: 0;
}

// Snapshot

export interface XferSnapshot {
  _id: string;
  parents: {
    project: string;
  };
  status: string;
  created: string;
  origin: XferOrigin;
}

// Operation common

export enum XferOriginType {
  User = 'user',
  Device = 'device',
  System = 'system',
}

export enum XferOverwrite {
  Never = 'never',
  Auto = 'auto',
  Always = 'always',
}

export enum XferReportFormat {
  JSONL = 'jsonl',
  CSV = 'csv',
}

export interface XferUrl {
  url?: string;
}

// Export

export enum ExportLevel {
  Project = 'project',
  Subject = 'subject',
  Session = 'session',
  Acquisition = 'acquisition',
}

export enum ExportStage {
  WaitingForSnapshot = 'waiting_for_snapshot',
  ScanningSnapshot = 'scanning_snapshot',
  ProcessingItems = 'processing_items',
  DeletingExtra = 'deleting_extra',
  CreatingManifest = 'creating_manifest',
}
export enum ExportItemStatus {
  Pending = 'pending',
  Running = 'running',
  Retry = 'retry',
  Canceling = 'canceling',
  Canceled = 'canceled',
  Success = 'success',
  Failed = 'failed',
}
export enum ExportUnzipPath {
  Original = 'original',
  Underscore = 'underscore',
  Basename = 'basename',
}

export interface ExportProgress {
  completed_bytes: number;
  completed_files: number;
  total_bytes: number;
  total_files: number;
  timestamp?: string;
}
export interface XferOrigin {
  id: string;
  type: XferOriginType;
}
export interface ExportRefs {
  project: string;
  snapshot: string;
  storage: string;
  connector?: string;
  schedule?: string;
  manifest_file?: string;
  blob_session?: string;
  rule_set?: string;
  log_file?: string;
  report_csv?: string;
}

export interface ExportRule {
  level: ExportLevel;
  include: string[];
  exclude: string[];
  path: string;
  unzip: boolean;
  metadata: boolean;
  unzip_path: ExportUnzipPath;
}

export interface ExportRuleFilter {
  name: string;
  operator: string;
  chips: string[];
  text: string;
  date: string;
  number: string;
  select: string;
}

export interface XferExport {
  _id: string;
  created: string;
  modified: string;
  type: string;
  priority: number;
  rerun: number;
  label: string;
  description?: string;
  origin: XferOrigin;
  refs: ExportRefs;
  rules: ExportRule[];
  overwrite_existing: XferOverwrite;
  delete_extra: boolean;
  dry_run?: boolean;
  started?: string;
  completed?: string;
  status_history: {
    status2: XferOperationStatus;
    timestamp: string;
  }[];
  storage_url?: string;
  status_reason?: string;
  stage?: ExportStage;
  progress: ExportProgress;
  ignore_conflicts: boolean;
  bytes_per_sec: number;
  files_per_sec: number;
  storage_override?: StorageConfigOverride;
  // optionally joined fields
  schedule?: {
    _id: string;
    cron: string;
    end: string;
    start: string;
  };
  snapshot?: XferSnapshot;
  storage?: XferStorage;
  status2: XferOperationStatus;
  blob_session?: {
    _id: string;
    created: string;
    deleted: string;
    modified: string;
    project_id: string;
    origin: {
      type: string;
      id: string;
    };
    open: boolean;
  };
  totals?: {
    timestamp: string;
    discovered_files: number;
    discovered_bytes: number;
    created_files: number;
    created_bytes: number;
    replaced_files: number;
    replaced_bytes: number;
    skipped_files: number;
    skipped_bytes: number;
    failed_files: number;
    failed_bytes: number;
    transferred_files: number;
    transferred_bytes: number;
    success_files: number;
    success_bytes: number;
    processed_files: number;
    processed_bytes: number;
    remaining_files: number;
    remaining_bytes: number;
  };
}

export interface XferExportTotals extends OperationProgressTotals {
  created_bytes: number;
  created_files: number;
  processed_files: number;
  remaining_bytes: number;
  remaining_files: number;
  replaced_bytes: number;
  replaced_files: number;
  skipped_bytes: number;
  success_bytes: number;
  timestamp: string;
  transferred_bytes: number;
  transferred_files: number;
}

export interface ExportProgress2 extends AddDataProgress {
  totals: XferExportTotals;
  items: ExportItem[];
  report_csv?: string;
}

export interface ExportItem {
  dst_path: string;
  src_path: string;
  status: ExportItemStatus;
  unzip?: number;
}

export interface ExportTask {
  src: string;
  dst?: string;
  status: ExportItemStatus;
  unzip?: number;
}

export interface XferSchedule {
  _id: string;
  created: string;
  modified: string;
  cron?: string;
  start?: string;
  end?: string;
  next_run?: string;
  last_run?: string;
  status?: string;
  error?: string;
  storage?: { url: string };
  latest_run?: { status: string };
}

// Create Export

export interface Export {
  label: string;
  description?: string;
  refs: {
    project: string;
    snapshot?: string;
    storage?: string;
    blob_session?: string;
  };
  rules?: ExportRule[];
  overwrite_existing?: string;
  delete_extra?: boolean;
  limit?: number;
  dry_run?: boolean;
  ignore_conflicts?: boolean;
  create_log?: boolean;
  create_manifest?: boolean;
  storage_override?: StorageConfigOverride;
}

export interface ExportPayload extends Export {
  rules?: ExportRule[];
}

// Imports

export interface ImportRefs {
  project: string;
  storage?: string;
  blob_session?: string;
  connector?: string;
  schedule?: string;
  report?: string;
  logfile?: string; // DEPRECATED replaced with report
}

export enum ImportLevel {
  Acquisition = 'acquisition',
}

export enum ImportMissingMeta {
  Fail = 'fail',
  Skip = 'skip',
}

export enum ImportConflictStrategy {
  Skip = 'skip',
  Update = 'update',
  Review = 'review',
}

export enum XferImportStatus {
  Pending = 'pending',
  Running = 'running',
  Cancelling = 'canceling',
  Cancelled = 'canceled',
  Success = 'success',
  Failed = 'failed',
}

export enum XferOperationStatus {
  Pending = 'pending',
  Running = 'running',
  Cancelling = 'cancelling',
  Cancelled = 'cancelled',
  Completed = 'completed',
  Failed = 'failed',
}

export enum XferImportExceptions {
  Conflicts = 'Conflicts',
  FileErrors = 'File Errors',
  Malware = 'Malware',
}

export enum XferExportExceptions {
  FileErrors = 'File Errors',
}

export interface ImportRule {
  level?: ImportLevel;
  include?: string[];
  exclude?: string[];
  type?: string;
  zip?: boolean;
  group_by?: string;
  mappings?: string[][];
  defaults?: string;
  overrides?: string;
}

export interface XferImport {
  _id: string;
  created: string;
  modified: string;
  label: string;
  description?: string;
  origin: XferOrigin;
  refs: ImportRefs;
  storage_override?: StorageConfigOverride;
  missing_meta: ImportMissingMeta;
  conflict_strategy: ImportConflictStrategy;
  rerun: number;
  status: XferImportStatus;
  status2: XferOperationStatus;
  totals: XferImportTotals;
  status_reason?: string;
  total_time: number;
  // optionally joined fields
  schedule?: {
    _id: string;
    cron: string;
    end: string;
    start: string;
  };
  storage_url?: string;
  dry_run?: boolean;
  limit?: number;
  fail_fast?: string;
  delete_on_success?: boolean;
  latest_run?: boolean;
  completed?: string;
  local?: boolean;
  can_rerun?: boolean;
  storage?: XferStorage;
  conflicts?: number;
  has_conflicts?: boolean;
  project?: {
    _id: string;
  };
}

export interface XferImportTotals extends OperationProgressTotals {
  created_acquisitions: number;
  created_sessions: number;
  created_subjects: number;
  malware_files: number;
  processed_bytes: number;
  remaining_bytes: number;
  remaining_files: number;
  review_files: number;
  skipped_acquisitions: number;
  skipped_bytes: number;
  skipped_sessions: number;
  skipped_subjects: number;
  success_bytes: number;
  updated_acquisitions: number;
  updated_sessions: number;
  updated_subjects: number;
}

export interface AddDataProgress {
  status2: XferOperationStatus;
  elapsed_time: number;
  remaining_time: number;
  totals: OperationProgressTotals;
  status_reason?: string;
}

export interface OperationProgressTotals {
  discovered_files: number;
  discovered_bytes: number;
  failed_files: number;
  failed_bytes: number;
  processed_bytes: number;
  processed_files: number;
  skipped_files: number;
  success_files: number;
  egress_bytes: number;
  egress_files: number;
}

export interface XferImportProgress extends AddDataProgress {
  totals: XferImportTotals;
  report?: string;
  logfile?: string; // DEPRECATED replaced with report
  status_reason?: string;
}

export enum XferUpsertResult {
  Created = 'created',
  Updated = 'updated',
  Skipped = 'skipped',
}

export interface XferImportCreate {
  label: string;
  description?: string;
  refs: ImportRefs;
  storage_override?: StorageConfigOverride;
  missing_meta?: ImportMissingMeta;
  conflict_strategy: ImportConflictStrategy;
  dry_run?: boolean;
  limit?: number;
  fail_fast?: string;
  delete_on_success?: boolean;
  deid?: boolean;
}

export interface XferConflict {
  _id: string;
  type: XferConflictType;
  message: string;
  level: XferConflictLevel;
  fields: Record<string, string>;
  upload_metadata: XferConflictUploadMetadata;
  upload_path: string;
  action: string;
  action_error?: string;
}

export interface XferConflictUploadMetadata {
  storage_prefix: string;
  storage_paths: string[];
  subject: {
    label: string;
  };
  session: {
    label: string;
  };
  acquisition: {
    label: string;
  };
  file: {
    name: string;
  };
}

export enum XferConflictLevel {
  Subject = 'subject',
  Session = 'session',
  Acquisition = 'acquisition',
  File = 'file',
}

export enum XferConflictType {
  FileNameAlreadyExists = 'file_name_already_exists',
  IDMatchAlreadyHasADifferentUid = 'id_match_already_has_a_different_uid',
  IDMatchBelongsToADifferentParent = 'id_match_belongs_to_a_different_parent',
  LabelMatchAlreadyHasADifferentUid = 'label_match_already_has_a_different_uid',
  LabelMatchConflictsWithTheIdMatch = 'label_match_conflicts_with_the_id_match',
  LabelMatchConflictsWithTheRoutingFieldMatch = 'label_match_conflicts_with_the_routing_field_match',
  LabelMatchConflictsWithTheUidMatch = 'label_match_conflicts_with_the_uid_match',
  MultipleLabelMatchesFound = 'multiple_label_matches_found',
  MultipleRoutingFieldMatchesFound = 'multiple_routing_field_matches_found',
  MultipleUidMatchesFound = 'multiple_uid_matches_found',
  RoutingFieldMatchAlreadyHasADifferentUid = 'routing_field_match_already_has_a_different_uid',
  UIDMatchBelongsToADifferentParent = 'uid_match_belongs_to_a_different_parent',
  UIDMatchConflictsWithTheIdMatch = 'uid_match_conflicts_with_the_id_match',
  UniqueIndexError = 'unique_index_error',
}

export enum XferConflictResolveMethod {
  AllowUIDDuplicate = 'allow_uid_duplicate',
  RejectIncoming = 'reject_incoming',
  RenameIncoming = 'rename_incoming',
  ReplaceExisting = 'replace_existing',
  Retry = 'retry',
}

export type XferConflictTypeResolveMethods = Record<XferConflictType, XferConflictResolveMethod[]>;

export interface XferConflictResolveInput {
  project: string;
  actions: XferConflictResolveActionInput[];
}

export interface XferConflictResolveActionInput {
  id: string;
  strategy: string;
}

export interface XferBlob {
  _id: string;
  project_id: string;
  path: string;
  size: number;
}

export interface XferBlobCreate {
  project_id: string;
  blob_session_id?: string;
  path: string;
  seq_no?: number;
  tar?: boolean;
}

export interface XferPendingBlob {
  _id: string;
  finish_url: string;
  upload_id?: string;
  abspath: string;
  path: string;
}

export interface XferBlobUploadResponse {
  upload_url: string;
  upload_headers?: { [key: string]: string } | null;
}

export interface XferBlobPart {
  part: number;
  etag?: string;
}

export interface XferBlobFinish {
  parts?: XferBlobPart[];
}

export interface XferBlobSessionCreate {
  project_id: string;
  local_path?: string;
}

export interface XferBlobSession {
  _id: string;
  project_id: string;
  open: boolean;
  label: string;
  local_path: string;
  last_abspath: string;
  last_seq_no: number;
  bytes: number;
  files: number;
}

interface XferConnectorInfo {
  version: string;
  privileged: boolean;
  fs_mounts: string[];
  clamav: boolean;
}

export interface XferConnector {
  _id: string;
  label: string;
  version: string;
  last_seen: string;
  disabled: boolean;
  info: XferConnectorInfo;
}

export interface XferConnectorResponse {
  total: number;
  count: number;
  results: XferConnector[];
  prev_before_id: string;
  next_after_id: string;
}

export interface XferVersion {
  version: string;
  latest_cli_version: string;
}

export enum XferRuleLevel {
  Site = 0,
  Group = 1,
  Project = 2,
}

export enum XferRuleType {
  Import = 'import',
  Export = 'export',
}

export interface XferRule {
  _id: string;
  type: XferRuleType;
  created: string;
  modified: string;
  description: string;
  version: number;
  refs: {
    group: string;
    project: string;
  };
  name: string;
  spec: {
    type: XferRuleType;
    rules: XferSpecRule[];
  };
  original: string;
  latest: boolean;
  default: boolean;
  fallback_default: boolean;
  built_in: boolean;
  history: XferRuleHistory[];
  created_by: {
    group: string;
    project: string;
  };
  modified_by: {
    group: string;
    project: string;
  };
  archived?: boolean;
  archived_at?: string;
  yaml: string;
  level: XferRuleLevel;
}

export interface XferSpecRule {
  level: string;
  include: string[];
  exclude: string[];
  type: string;
  zip: boolean;
  zip_single: boolean;
  mappings: string[][];
  dicom_instance_name: string;
  dicom_group_by: string[];
  dicom_split_localizer: boolean;
  group_by?: string[];
  defaults?: Record<string, string>;
  overrides?: Record<string, string>;
}

export type XferRuleHistory = {
  [key: string]: string | XferRuleHistory;
};

export interface XferRuleCreate {
  type: XferRuleType;
  name: string;
  description: string;
  default: boolean;
  refs?: {
    group?: string;
    project?: string;
  };
  spec?: {
    type: string;
    rules?: XferSpecRule[];
  };
  yaml?: string;
}

export interface XferRuleSchema {
  import: { schema: XferRuleImportSchema; yaml_scaffold: string };
  export: { schema: XferRuleExportSchema; yaml_scaffold: string };
}

export interface XferRuleImportSchema {
  $defs: {
    ImportLevel: {
      description: string;
      enum: string[];
      title: string;
      type: string;
    };
    ImportRuleCreate: {
      additionalProperties: false;
      description: string;
      properties: XferSpecRule;
      required: string[];
      title: string;
      type: string;
    };
    MetaValue: { type: string; items: Record<string, string>; additionalProperties: Record<string, string> };
  };
  additionalProperties: false;
  description: string;
  properties: {
    type: {
      const: string;
      title: string;
      type: string;
    };
    rules: {
      description: string;
      items: {
        $ref: string;
      };
      minItems: number;
      title: string;
      type: string;
    };
  };
  required: string[];
  title: string;
  type: string;
}

export interface XferRuleExportSchema {
  $defs: {
    ExportLevel: {
      description: string;
      enum: string[];
      title: string;
      type: string;
    };

    ExportRuleCreate: {
      additionalProperties: false;
      description: string;
      properties: XferSpecRule;
      title: string;
      type: string;
    };

    UnzipPath: {
      description: string;
      enum: string[];
      title: string;
      type: string;
    };
  };

  additionalProperties: false;
  description: string;
  properties: {
    type: {
      const: string;
      title: string;
      type: string;
    };
    rules: {
      description: string;
      items: {
        $ref: string;
      };
      minItems: number;
      title: string;
      type: string;
    };
  };
  required: string[];
  title: string;
  type: string;
}

export interface XferStoreSCPS {
  _id: string;
  created: string;
  modified: string;
  connector: string;
  host: string;
  ports: number[];
  ca_bundle: XferStoreSCPsPemCertificate;
  trusted_client_cas: {
    _id: string;
    cert: XferStoreSCPsPemCertificate;
  }[];
  leaf_cert: XferStoreSCPsPemCertificate;
  aets: string[];
  additional_aets: string[];
  effective_aets: string[];
  active_studies: number;
  uptime_secs: number;
  last_heartbeat: string;
}

export interface XferStoreSCU {
  _id: string;
  created: string;
  modified: string;
  name: string;
  aet: string;
  host: string;
  port?: number;
  cert: XferStoreSCPsPemCertificate;
  cert_expiration: string;
  store_scp: string;
  enabled: boolean;
  created_by: {
    type: string;
    id: string;
  };
  modified_by: {
    type: string;
    id: string;
  };
  log: {
    ts: number;
    message: string;
  }[];
}

export interface XferStoreSCPsPemCertificate {
  type: string;
  pem: string;
}
